<template>
  <dynamic-object-form-structure
    :object-uuid="objectUUID"
    :object-layer="objectLayer"
    :object-data="objectData"
    :loading-structure="loadingStructure"
    :loading-save="loadingSave"
    :permission-name="form.permission"
    :object-type-translation-key="form.name_translation"
    :tabs="visibleTabs"
    :active-tab="activeTab"
    :action-type="actionType"
    :map-element="$parent.$refs.formsContainer"
    :previous-pos-x="previousPosX"
    :previous-pos-y="previousPosY"
    :saveIsDisabled="!required.filled"
    :data-was-changed="dataWasChanged"
    :fields-still-need-filled="required.stillNeedFilled"
    @updateActiveTab="activeTab = $event"
    @changeViewMode="changeViewModeOfObject"
    @openDetails="openDetails"
    @refresh="refresh"
    @clearData="clearData"
    @save="saveObject"
    @close="closeForm"
  >
    <template v-slot:formFields>
      <div
        v-show="visibleTabs[activeTab].type === 'table'"
        class="form tab-content"
      >
        <customer-premises-tab
          v-if="customerPremisesTable.loaded"
          :layer="'fc_customer_premises'"
          :editMode="!readOnly"
          :originalObjects="
            customerPremisesTable.objects ? customerPremisesTable.objects : []
          "
          :buildingUuid="
            customerPremisesTable.buildingUuid
              ? customerPremisesTable.buildingUuid
              : ''
          "
          :max-height="400"
          @updateTotalOfHPs="updateTotalOfHPs"
        />
      </div>

      <div
        v-show="visibleTabs[activeTab].type === 'photos'"
        class="form tab-content"
      >
        <forms-photo-section
          :disabled="readOnly"
          :images-list="imagesList"
          :previous-images="previousImages"
          @updatePhoto="updatePhoto"
          @removePhoto="addToPhotosToRemove"
        />
      </div>

      <div
        v-show="visibleTabs[activeTab].type === 'form'"
        class="form tab-content"
      >
        <template v-for="field in visibleTabs[activeTab].fields">
          <input-field
            v-if="
              field.var_name === 'bd_bld_city_code' &&
                field.select_list_name === 'list_cities_portugal'
            "
            :key="visibleTabs[activeTab].code + field.label + field.order"
            :value="getValueInObject(field.var_name, field.input_type)"
            :label="field.label_client ? field.label_client : field.label"
            :variable-name="field.var_name"
            inputType="other"
            :size="field.size"
            :required="field.required"
            :required-filled="required.filled"
            :hide="field.hide"
            :fieldDisabled="field.disable"
            :tabDisabled="!userCanEditTabContent(visibleTabs[activeTab])"
            :formDisabled="readOnly"
          >
            <div>
              <treeselect
                :value="objectData.bd_bld_city_code"
                :options="portugalCitiesOptions"
                @input="setValueCityCode"
                :normalizer="portugalCitiesOptionsNormalizer"
                :disabled="
                  readOnly ||
                    !userCanEditTabContent(visibleTabs[activeTab]) ||
                    field.disable
                "
                class="form-input-select"
                disableFuzzyMatching
              />
            </div>
          </input-field>

          <input-field
            v-else-if="
              field.var_name === 'bd_demand_point_locale_code' &&
                field.select_list_name === 'list_cities_locale_portugal'
            "
            :key="visibleTabs[activeTab].code + field.label + field.order"
            :value="getValueInObject(field.var_name, field.input_type)"
            :label="field.label_client ? field.label_client : field.label"
            :variable-name="field.var_name"
            inputType="other"
            :size="field.size"
            :required="field.required"
            :required-filled="required.filled"
            :hide="field.hide"
            :fieldDisabled="field.disable"
            :tabDisabled="!userCanEditTabContent(visibleTabs[activeTab])"
            :formDisabled="readOnly"
          >
            <div>
              <treeselect
                :value="objectData.bd_demand_point_locale_code"
                :options="portugalCitiesLocaleOptions"
                @input="setValueCityLocaleCode"
                :normalizer="portugalCitiesLocaleOptionsNormalizer"
                :disabled="
                  readOnly ||
                    !userCanEditTabContent(visibleTabs[activeTab]) ||
                    field.disable ||
                    !objectData.bd_bld_city_code
                "
                class="form-input-select"
                disableFuzzyMatching
              />
            </div>
          </input-field>

          <input-field
            v-else
            :key="visibleTabs[activeTab].code + field.label + field.order"
            ref="formInputField"
            :value="getValueInObject(field.var_name, field.input_type)"
            @input="setValueInObject(field.var_name, $event, field.input_type)"
            :label="field.label_client ? field.label_client : field.label"
            :variable-name="field.var_name"
            :inputType="field.input_type"
            :size="field.size"
            :required="field.required"
            :required-filled="required.filled"
            :selectOptions="field.select_list_name"
            :radioOptions="field.radio_options"
            :fieldDisabled="field.disable"
            :tabDisabled="!userCanEditTabContent(visibleTabs[activeTab])"
            :formDisabled="readOnly"
            :hide="field.hide"
            :checkboxOptions="field.checkbox_options"
            :object-data="objectData"
            :labelIsLocked="objectData.bd_label_locked"
            :user-can-unlock-label="!userHasFibercloudPermission(form.permission + '_manage_label')"
            @toggle-label-lock="changeLabelMode"
            @updateCheckboxValue="updateCheckboxValue($event)"
            @removePhoto="addToPhotosToRemove($event)"
          />
        </template>
      </div>

      <building-operator-section
        v-show="visibleTabs[activeTab].code === 'building_operator'"
        :original-operators="objectData"
        @updateOperatorsList="setValueInObject('building_operator', $event)"
        @updateNumberOperators="
          setValueInObject('bd_demand_point_num_operators', $event)
        "
        :disabled="readOnly"
      />

      <building-typification
        v-show="visibleTabs[activeTab].code === 'building_typification'"
        :object-data="objectData"
        :disabled="readOnly"
        @updateEntries="setValueInObject('building_typification', $event)"
      />
      <custom-fields-tab
        v-if="visibleTabs[activeTab].code === 'custom_fields'"
        :customFields="customFields.custom_fields"
        :objectData="objectData.custom_fields"
        :actionType="actionType"
        :disabled="readOnly"
        @updateValue="setValueInObject"
        :requiredFilled="required.filled"
      />

      <vertical-planning-tab
        v-if="visibleTabs[activeTab].code === 'building_vertical'"
        :ground-floors-prop="buildingVertical.groundFloors"
        :underground-floors-prop="buildingVertical.undergroundFloors"
        :columns-prop="buildingVertical.columns"
        :technical-locals-prop="buildingVertical.technicalLocals"
        :action-type="actionType"
        @update-vertical-planning="updateVerticalPlanning($event)"
      />
    </template>

    <template v-slot:dialogBox>
      <app-dialog-box
        :open="dialog"
        :isPersistent="true"
        :title="$t('TechnicalSiteDialog.createTechnicalLocalTitle')"
        :content="$t('TechnicalSiteDialog.createTechnicalLocalText')"
        :main-action-label="$t('Yes')"
        @mainAction="createTechnicalLocal('Building')"
        :secondary-action-label="$t('No')"
        @secondaryAction="closeDialog"
      />
    </template>
  </dynamic-object-form-structure>
</template>

<script>
import { bus } from "@/main";
import { dynamicObjectFormsMixin } from "@/mixins/forms/dynamicObjectForms/dynamicObjectFormsMixin";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import DynamicObjectFormStructure from "../_Templates/DynamicObjectFormStructure";
import AppDialogBox from "../../../_Molecules/AppDialogBox";
import FormInput from "../FormInput";
import CustomerPremisesTab from "../_SpecialTabs/CustomerPremises/CustomerPremisesTab";
import FormsPhotoSection from "../_SpecialTabs/Photos/PhotosForm";
import InputField from "../_Molecules/InputField";
import BuildingOperatorSection from "@/components/Home/Forms/_SpecialTabs/Buildings/Operators";
import BuildingTypification from "@/components/Home/Forms/_SpecialTabs/Buildings/Typification/BuildingTypification";
import CustomFieldsTab from "@/components/Home/Forms/_SpecialTabs/CustomFields/CustomFieldsTab.vue";
import VerticalPlanningTab from "@/components/Home/Forms/_SpecialTabs/Buildings/Vertical/VerticalPlanning.vue";

export default {
  name: "BuildingForm",

  components: {
    InputField,
    FormsPhotoSection,
    CustomerPremisesTab,
    FormInput,
    AppDialogBox,
    DynamicObjectFormStructure,
    BuildingOperatorSection,
    BuildingTypification,
    CustomFieldsTab,
    VerticalPlanningTab
  },

  mixins: [dynamicObjectFormsMixin,checkPermissionsMixin],

  props: {
    /**
     */
    linkObjectToPathway: {
      type: Object
    }
  },

  data() {
    return {
      customerPremisesTable: {
        loaded: false,
        objects: [],
        buildingUuid: "",
        existingNumber: 0
      },
      minNumberHPsPerMDU: null,
      technicalSiteData: {},
      portugalCitiesLocaleOptions: null,

      dialog: false,

      buildingVertical: {
        columns: [],
        groundFloors: [],
        undergroundFloors: [],
        technicalLocals: []
      }
    };
  },

  computed: {
    hasCustomerPremises() {
      return this.customerPremisesTable.existingNumber > 0;
    },

    buildingType() {
      let totalHPs =
        Number(this.objectData.bd_demand_point_hab_bals) +
        Number(this.objectData.bd_demand_point_com_bals);
      if (this.objectData.bd_demand_point_pub_bals) {
        totalHPs += Number(this.objectData.bd_demand_point_pub_bals);
      }
      if (this.objectData.bd_demand_point_emp_bals) {
        totalHPs += Number(this.objectData.bd_demand_point_emp_bals);
      }
      if (totalHPs >= this.minNumberHPsPerMDU) {
        return "MDU";
      } else if (totalHPs < this.minNumberHPsPerMDU && totalHPs >= 0) {
        return "SDU";
      }
    },

    portugalCitiesOptions() {
      return this.$store.getters.getListOfOptionsByListName(
        "list_cities_portugal",
        [this.objectData.bd_bld_city_code]
      );
    }
  },

  watch: {
    buildingType(newValue) {
      this.objectData.bd_type = newValue;
    },

    hasCustomerPremises(newValue) {
      let fields = this.form.tabs[0].fields;
      let resHPField = fields.find(
        field => field.var_name === "bd_demand_point_hab_bals"
      );
      let comHPField = fields.find(
        field => field.var_name === "bd_demand_point_com_bals"
      );
      if (newValue) {
        resHPField.disable = comHPField.disable = true;
      } else {
        resHPField.disable = comHPField.disable = false;
      }
    },

    "objectData.bd_bld_city_code"(newValue) {
      this.updatePortugalCitiesLocaleOptions(newValue);
    },

    "objectData.extra_fields.bd_category_type"(newValue) {
      if (newValue === "R") {
        if (!this.dataReceived.bd_demand_point_hab_bals) {
          this.setValueInObject("bd_demand_point_hab_bals", 1);
        }
        if (!this.dataReceived.bd_demand_point_com_bals) {
          this.setValueInObject("bd_demand_point_com_bals", 0);
        }
      } else if (newValue === "C") {
        if (!this.dataReceived.bd_demand_point_com_bals) {
          this.setValueInObject("bd_demand_point_com_bals", 1);
        }
        if (!this.dataReceived.bd_demand_point_hab_bals) {
          this.setValueInObject("bd_demand_point_hab_bals", 0);
        }
      } else {
        if (!this.dataReceived.bd_demand_point_com_bals) {
          this.setValueInObject("bd_demand_point_com_bals", 0);
        }
        if (!this.dataReceived.bd_demand_point_hab_bals) {
          this.setValueInObject("bd_demand_point_hab_bals", 0);
        }
      }
    },

    /**
     * Parses the vertical planning data once the objectData.building_floors value changes
     *
     * @param newValue - the new value of building_floors
     */
    objectData(newValue) {
      if (!!newValue && this.objectData.building_floors.length > 0) {
        this.buildingVertical.columns = structuredClone(this.objectData.building_columns)
        let groundFloors = [];
        let undergroundFloors = [];
        if(this.objectData.building_columns.length > 0){
          this.cleanColumnsVariables();
        }
        if(this.objectData.technicallocals){
          this.clearTechnicalLocal();
        }
        this.cleanFloorsVariables();
        this.objectData.building_floors.forEach(floor => {
          if (floor.index < 0) {
            undergroundFloors.push(floor)
          } else {
            groundFloors.push(floor)
          }
        });
        this.buildingVertical.groundFloors = groundFloors;
        this.buildingVertical.undergroundFloors = undergroundFloors;
      }
    },
  },

  mounted() {
    this.getNetworkRuleMDU();
    if (Object.keys(this.dataReceived).length !== 0) {
      this.customerPremisesTable.objects = this.dataReceived.customer_premises;
      this.customerPremisesTable.buildingUuid = this.dataReceived.bd_uuid;
    }
    this.customerPremisesTable.loaded = true;
    bus.$on("updateNumberCustomerPremises", numberObjects => {
      this.customerPremisesTable.existingNumber = numberObjects;
    });
  },

  destroyed() {
    bus.$off("updateNumberCustomerPremises");
  },

  methods: {
    tabIsEnabled(tab) {
      if (tab.code === "building_typification") {
        return (
          (this.objectData.hasOwnProperty("bd_demand_point_habitation_type") &&
            this.objectData.bd_demand_point_habitation_type === 1) ||
          (this.objectData.hasOwnProperty("bd_type") &&
            this.objectData.bd_type === "MDU") ||
          this.buildingType === "MDU"
        );
      } else if (tab.code === "building_vertical") {
        return this.buildingType === "MDU"
      }
      return true;
    },

    updateTotalOfHPs(totalOfHPs) {
      if (this.hasCustomerPremises) {
        this.setValueInObject(
          "bd_demand_point_hab_bals",
          totalOfHPs.totalHab + ""
        );
        this.setValueInObject(
          "bd_demand_point_com_bals",
          totalOfHPs.totalCom + ""
        );
      }
    },

    createObject() {
      this.createDataToSend();
      this.removeUnneededFields();

      this.CREATE_OBJECTS({
        endpoint: this.form.endpoint,
        data: [this.dataToSend]
      })
        .then(resp => {
          if (this.linkObjectToPathway.split) {
            bus.$emit("reset-pathway-and-split-data");
          }

          this.customerPremisesTable.buildingUuid = resp.data.data[0].bd_uuid;
          bus.$emit("save_customer_premises", promise => {
            promise.then(() => {
              if (this.objectData.bd_type === "MDU") {
                this.dialog = true;
                this.technicalSiteData = resp.data.data[0];
              } else {
                this.finishObjectCreation();
              }
            });
          });

        })
        .catch(error => {
          this.$toasted.global.toast_error(error.response.data.data);
          this.loadingSave = false;
        });
    },

    createDataToSend() {
      this.dataToSend = {
        bd_status: this.objectData.bd_demand_point_status,
        technical_site: {
          status: this.objectData.bd_demand_point_status,
          logical_type: this.objectData.bd_type,
          physical_type: "BAT",
          node: { text_geom: this.wkt, projection: 3857 }
        },
        tpa_services: this.objectData.tpa_services,
      };
      if (this.linkObjectToPathway.split) {
        this.dataToSend.technical_site.node.delete_pathways_association =
          "false";
        this.dataToSend.technical_site.node.pathway = this.linkObjectToPathway.pathway;
      }
      this.updateTypification();
      this.placeObjectDataInDataToSend();
    },

    async editObject() {
      try {
        if (this.imagesToRemove.length > 0) {
          await this.removePhotos();
        }

        this.removeUnneededFields();
        await this.EDIT_OBJECTS({
          endpoint: this.form.endpoint,
          data: [this.dataToSend]
        });
        bus.$emit("save_customer_premises", promise => {
          promise.then(() => {
            this.updateGeoServer();
            this.closeForm();
            this.$toasted.global.toast_success(this.$i18n.t("EditionSuccess"));
          });
        });
        
      } catch (error) {
        this.$toasted.global.toast_error(error.response.data.data);
      } finally {
        this.loadingSave = false;
      }
    },

    prepareEditedDataToSend() {
      this.dataToSend.uuid = this.dataReceived[this.initials + "uuid"];
      this.updateTypification();
    },

    updateTypification() {
      if (
        this.form.tabs.some(tab => tab.code === "building_typification") &&
        this.dataToSend.hasOwnProperty("demand_point_habitation_type") &&
        this.dataToSend.demand_point_habitation_type === 0 &&
        this.objectData.hasOwnProperty("building_typification") &&
        this.objectData.building_typification.length > 0
      ) {
        this.dataToSend.building_typification = [];
      }
    },

    portugalCitiesOptionsNormalizer(node) {
      return {
        id: node.code,
        label: node.municipality_label
      };
    },

    portugalCitiesLocaleOptionsNormalizer(node) {
      return {
        id: node.code,
        label: node.libelle
      };
    },

    setValueCityCode(cityCode) {
      let city = null;
      if (cityCode) {
        city = this.portugalCitiesOptions.find(city => city.code == cityCode);
        if (city) {
          city = city.municipality_label;
        }
      }

      this.setValueInObject("bd_bld_city", city);
      this.setValueInObject("bd_bld_city_code", cityCode);

      this.resetLocale();
    },

    setValueCityLocaleCode(localeCode) {
      if (localeCode) {
        let locale = this.portugalCitiesLocaleOptions.find(
          locale => locale.code == localeCode
        );
        if (locale) {
          this.setValueInObject("bd_demand_point_locale", locale.libelle);
        }
        this.setValueInObject("bd_demand_point_locale_code", localeCode);
      } else {
        this.resetLocale();
      }
    },

    resetLocale() {
      this.setValueInObject("bd_demand_point_locale", null);
      this.setValueInObject("bd_demand_point_locale_code", null);
    },

    updatePortugalCitiesLocaleOptions(cityCode) {
      if (cityCode) {
        let filters = [
          {
            field: "city_portugal_id",
            value: cityCode
          }
        ];
        this.portugalCitiesLocaleOptions = this.getFilteredListOptions(
          "portugalCitiesLocaleOptions",
          "list_cities_locale_portugal",
          filters
        );
      } else {
        this.portugalCitiesLocaleOptions = null;
      }
    },

    /**
     * Updates the value of the buildingVertical variable by cloning the received data
     */
    updateVerticalPlanning(data) {
      this.buildingVertical = structuredClone(data);
    },

    /**
     * Cleans the name of the received variable
     *
     * @param variableName - the name of the variable to be cleaned
     * @param splitter - the value used to split the variable name
     *
     * @return {String} - the cleaned variable name
     */
    cleanVariableName(variableName, splitter) {
      let splitName = variableName.split(splitter);
      let readyName = '';
      if (splitName.length === 1) {
        readyName = splitName[0]
      } else {
        for (let i = 1; i < splitName.length; i++) {
          readyName += splitName[i]
          if (i !== splitName.length - 1) {
            readyName += '_'
          }
        }
      }
      return readyName;
    },

    /**
     * Cleans the names of the variables of the columns' data
     */
    cleanColumnsVariables() {
      for (let k = 0; k < this.buildingVertical.columns.length; k++) {
        let readyColumn = Object.fromEntries(Object.entries(this.buildingVertical.columns[k]).map(([k,v]) => [k.replace("bc_", ""), v]))
        for (let i = 0; i < readyColumn.floorboxes.length; i++) {
          let readyFloorbox = Object.fromEntries(Object.entries(readyColumn.floorboxes[i]).map(([k,v]) => [k.replace("fbx_", ""), v]));
          let floorboxFloor = this.objectData.building_floors.find(floor => floor.bfl_id === readyFloorbox.floor_id);
          readyFloorbox.floor_uuid = floorboxFloor.bfl_uuid;
          readyFloorbox.floor = '' + floorboxFloor.bfl_index;
          readyFloorbox.column_uuid = readyColumn.uuid;
          const {cable_material_id, cable_owner_id, ...rest} = readyFloorbox;
          const cable_in = readyFloorbox.closure.cable_in.length > 0 ? readyFloorbox.closure.cable_in[0] : null;
          let cleanFloorbox;
          if(cable_in){
            cleanFloorbox = {
              ...rest, 
              cable_in_material_id: cable_in.cb_material_id, 
              cable_in_owner_id: cable_in.cb_owner,
              cable_in_length: cable_in.cb_measured_length, 
              cable_out_material_id: cable_material_id,
              cable_out_owner_id: cable_owner_id,
            };
          }else{
            cleanFloorbox = {
              ...rest, 
              cable_in_material_id: null, 
              cable_in_owner_id: null, 
              cable_in_length: null,
              cable_out_material_id: cable_material_id,
              cable_out_owner_id: cable_owner_id,
            };
          }
          readyColumn.floorboxes[i] = cleanFloorbox;
        }
        this.buildingVertical.columns[k] = readyColumn
      }
      this.buildingVertical.columns.sort((columnA, columnB) => parseInt(columnA.index) - parseInt(columnB.index));
    },

    /**
     * Cleans the names of the variables of the floors' data
     */
    cleanFloorsVariables() {
      for (let k = 0; k < this.objectData.building_floors.length; k++) {
        let readyFloor = Object.fromEntries(Object.entries(this.objectData.building_floors[k]).map(([k,v]) => [k === "building_fractions" ? "fractions" : k.replace("bfl_", ""), v]))
        for (let i = 0; i < readyFloor.fractions.length; i++) {
          let readyFraction = Object.fromEntries(Object.entries(readyFloor.fractions[i]).map(([k,v]) => [k.replace("bfr_", ""), v]));
          readyFraction.floor_uuid = readyFloor.uuid;
          const {cable_in, ...rest} = readyFraction
          let cleanFraction;
          if(cable_in){
            cleanFraction = {
              ...rest, 
              cable_in_material_id: cable_in.cb_material_id, 
              cable_in_owner_id: cable_in.cb_owner,
              cable_in_length: cable_in.cb_measured_length,
            };
          }else{
            cleanFraction = {...rest};
          }
          readyFloor.fractions[i] = cleanFraction;
          let floorbox = null;
          for (let k = 0; k < this.objectData.building_columns.length; k++) {
            const column = this.objectData.building_columns[k];
            floorbox = column.floorboxes.find(fbx => fbx.fbx_cl_id === cleanFraction.closure_id);
            if (floorbox) {
              readyFloor.fractions[i].floorbox_color = floorbox.fbx_color;
              readyFloor.fractions[i].floorbox_label = floorbox.fbx_label;
              readyFloor.fractions[i].floorbox_uuid = floorbox.closure.cl_uuid;
              break;
            } else continue;
          }
        }
        readyFloor.fractions.sort((fractionA, fractionB) => parseInt(fractionA.number) - parseInt(fractionB.number));

        this.objectData.building_floors[k] = readyFloor
      }
    },
    clearTechnicalLocal(){
      this.buildingVertical.technicalLocals = [
        {
          uuid: this.objectData.technicallocals.tl_uuid,
          id: this.objectData.technicallocals.tl_id,
          status: this.objectData.technicallocals.tl_status,
          owner: this.objectData.technicallocals.tl_owner,
          label: this.objectData.technicallocals.tl_label,
          comment: this.objectData.technicallocals.tl_comment
        }
      ];
    },
    /**
     * Removes fields in the data not needed by the backend and prepares the vertical planning object to be sent
     */
    removeUnneededFields() {
      this.dataToSend.building_vertical = {
        columns: this.buildingVertical.columns,
        floors: this.buildingVertical.groundFloors.concat(this.buildingVertical.undergroundFloors)
      };
      this.dataToSend.technical_locals = [...this.buildingVertical.technicalLocals];
      
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/Home/Forms/ObjectForms/BuildingForm.vue